import { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import OnboardingWizard from "@/components/OnboardingWizard";
import RoleWalkthrough from "@/components/RoleWalkthrough";
import { usePermissions } from "@/hooks/usePermissions";
import { routePermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldAlert } from "lucide-react";


const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { session, loading } = useAuth();
  const location = useLocation();
  const { can, isLoading: rolesLoading } = usePermissions();
  const required = routePermission(location.pathname);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace />;

  if (required && rolesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (required && !can(required)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-3">
          <span className="mx-auto h-12 w-12 rounded-full bg-destructive/10 text-destructive grid place-items-center">
            <ShieldAlert className="h-6 w-6" />
          </span>
          <h1 className="text-lg font-semibold">Not permitted · অনুমতি নেই</h1>
          <p className="text-sm text-muted-foreground">
            Your role does not include the “{required}” permission. Ask the practice
            owner to update your access.
          </p>
          <Button asChild variant="outline"><Link to="/app">Back to dashboard</Link></Button>
        </Card>
      </div>
    );
  }

  return (
    <>
      {children}
      <OnboardingWizard />
      <RoleWalkthrough />
    </>
  );
};


export default ProtectedRoute;
